import { useState, useContext, useMemo, useCallback } from 'react';
import axios from 'src/axios';
import { AppContext } from 'src/store/ContextStore';
import { EVENTS } from 'src/components/admin/appReducer';
import { toStoredDate } from 'src/util/util';
import { SuggestionFamilyContext } from 'src/components/UI/clinicalSuggestions/SuggestionFamilyContext';
import {
  matchSubject,
  memberKey,
  targetOf
} from 'src/components/UI/clinicalSuggestions/relativeMatch';

// Suggested Clinical Terms for one clinical-record form (TGCLOUD-1845).
//
// The analysis answers per PERSON: `subjects` = [{ key, relation, label,
// name, findings: [{ kind, id, name, status, preselect, evidence, … }] }].
// The patient's definite findings start ticked; a relative's start ticked only
// when the pedigree has exactly one person who fits (relativeMatch.js) —
// otherwise the clinician picks who they are, or adds nothing for them.

const findingKey = (subjectKey, f) => `${subjectKey}|${f.kind}|${f.id}`;

// A pre-1845 answer (three flat lists for the patient) as subjects.
function subjectsOf(data) {
  if (Array.isArray(data?.subjects)) return data.subjects;
  const as = (kind) => (x) => ({
    ...x,
    kind,
    status: 'present',
    preselect: false,
    evidence: []
  });
  return [
    {
      key: 'self',
      relation: 'self',
      label: 'This patient',
      name: null,
      findings: [
        ...(data?.phenotypes || []).map(as('phenotype')),
        ...(data?.diseases || []).map(as('disease')),
        ...(data?.genotypes || []).map(as('genotype'))
      ]
    }
  ];
}

// Findings from several uploads and the typed note, merged per person.
//
// One identity check is kept — the last that found a mismatch — and it names
// every uploaded file whose own check found one (`sources`), so with several
// documents in play the warning can say WHICH may be about someone else
// (TGCLOUD-1850).
function mergeAnalyses(list) {
  const subjects = new Map();
  const familyNotes = [];
  let identityCheck = null;
  const identitySources = [];
  let terminology = null;
  let engine = null;
  for (const a of list) {
    if (!a) continue;
    engine = engine || a.engine || null;
    terminology = a.terminology || terminology;
    if (
      a.identityCheck &&
      (a.identityCheck.dobMismatch || a.identityCheck.surnameMismatch)
    ) {
      identityCheck = a.identityCheck;
      if (a.sourceName) identitySources.push(a.sourceName);
    }
    familyNotes.push(...(a.familyNotes || []));
    for (const s of a.subjects || []) {
      if (!subjects.has(s.key)) subjects.set(s.key, { ...s, findings: [] });
      const into = subjects.get(s.key);
      for (const f of s.findings || []) {
        if (!into.findings.some((g) => g.kind === f.kind && g.id === f.id))
          into.findings.push(f);
      }
    }
  }
  return {
    subjects: [...subjects.values()],
    familyNotes,
    identityCheck: identityCheck
      ? { ...identityCheck, sources: identitySources }
      : null,
    terminology,
    engine
  };
}

export function useClinicalSuggestions({
  familyMemberId,
  isProband,
  onAccepted
}) {
  const { content, dispatchUpdate } = useContext(AppContext);
  const family = useContext(SuggestionFamilyContext) || {};
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const user_id = user?.user_id || user?._id;

  const [attachmentAnalyses, setAttachmentAnalyses] = useState([]);
  const [noteAnalysis, setNoteAnalysis] = useState(null);
  // How many analyses are in flight. Several uploads can each be analysed
  // while the typed note is too; a single boolean was cleared by whichever
  // finished first, hiding "Analysing…" while others were still running.
  const [analysesInFlight, setAnalysesInFlight] = useState(0);
  const analyzingNote = analysesInFlight > 0;
  const [savingNote, setSavingNote] = useState(false);
  // findingKey → ticked. Absent means "use the default" (see isSelected).
  const [ticks, setTicks] = useState({});
  // subjectKey → memberKey, or '' for "add nothing for this person".
  const [targets, setTargets] = useState({});
  const [lastAccept, setLastAccept] = useState(null);
  // The HPO release the latest suggestions were matched against, for the
  // footer the HPO licence asks for (TGCLOUD-1846).
  const [hpoRelease, setHpoRelease] = useState(null);

  const analysis = useMemo(
    () => mergeAnalyses([...attachmentAnalyses, noteAnalysis]),
    [attachmentAnalyses, noteAnalysis]
  );

  const members = useMemo(() => family.members || [], [family.members]);
  const selectedMember = family.selected || null;
  const matches = useMemo(() => {
    const out = {};
    for (const s of analysis.subjects) {
      if (s.key === 'self' || s.relation === 'partner') continue;
      out[s.key] = selectedMember
        ? matchSubject(
            { relation: s.relation, name: s.name },
            selectedMember,
            members
          )
        : { preMatch: null, candidates: [], others: [] };
    }
    return out;
  }, [analysis.subjects, members, selectedMember]);

  const targetFor = useCallback(
    (subjectKey) => {
      if (subjectKey === 'self') return 'self';
      if (subjectKey in targets) return targets[subjectKey] || null;
      const pre = matches[subjectKey]?.preMatch;
      return pre ? memberKey(pre) : null;
    },
    [targets, matches]
  );

  const isSelected = useCallback(
    (subjectKey, f) => {
      if (f.alreadyRecorded) return false;
      const k = findingKey(subjectKey, f);
      if (k in ticks) return ticks[k];
      if (
        subjectKey !== 'self' &&
        !(targets[subjectKey] ?? matches[subjectKey]?.preMatch)
      )
        return false;
      return !!f.preselect;
    },
    [ticks, targets, matches]
  );

  const setTarget = (subjectKey, key) =>
    setTargets((prev) => ({ ...prev, [subjectKey]: key || '' }));

  const toggleFinding = (subjectKey, f) =>
    setTicks((prev) => ({
      ...prev,
      [findingKey(subjectKey, f)]: !isSelected(subjectKey, f)
    }));

  const selectedItems = useMemo(() => {
    const out = [];
    for (const s of analysis.subjects) {
      const t = targetFor(s.key);
      if (!t) continue;
      for (const f of s.findings)
        if (isSelected(s.key, f))
          out.push({ subject: s, finding: f, target: t });
    }
    return out;
  }, [analysis.subjects, targetFor, isSelected]);

  // `sourceName` names the uploaded file a 'pdf-upload' text came from.
  const analyzeText = async (text = '', source = 'blur', sourceName = null) => {
    const trimmed = (text || '').trim();
    if (!trimmed) {
      if (source !== 'pdf-upload') setNoteAnalysis(null);
      return;
    }
    setAnalysesInFlight((n) => n + 1);
    try {
      const res = await axios.post('/clinical-notes/analyze-note', {
        noteText: trimmed,
        userId: user_id,
        familyMemberId,
        isProband: !!isProband
      });
      if (res.data.success) {
        const incoming = { ...res.data, subjects: subjectsOf(res.data) };
        // Only an answer carrying HPO-derived suggestions says anything about
        // the release; a genes-only answer leaves the one already shown, which
        // still names the source of the phenotypes and diseases on screen.
        const hpoDerived = incoming.subjects.some((s) =>
          s.findings.some((f) => f.kind === 'phenotype' || f.kind === 'disease')
        );
        if (hpoDerived) setHpoRelease(res.data.hpoRelease || null);
        if (source === 'pdf-upload')
          setAttachmentAnalyses((prev) => [
            ...prev,
            { ...incoming, sourceName }
          ]);
        else setNoteAnalysis(incoming);
      }
    } catch (err) {
      console.warn('Analyze-note request failed:', err?.message || err);
    } finally {
      setAnalysesInFlight((n) => Math.max(0, n - 1));
    }
  };

  const handleTextBlur = (e) =>
    analyzeText(e?.target?.value || '', 'text-blur');
  // Once per uploaded file, with that file's own text and name.
  const handleAttachmentText = (text, sourceName = null) =>
    analyzeText(text, 'pdf-upload', sourceName);

  const acceptSuggestions = async ({ clinicalNoteDate, clinician } = {}) => {
    if (!selectedItems.length) return { success: false };
    const memberByKey = new Map(members.map((m) => [memberKey(m), m]));
    const items = [];
    for (const { subject, finding: f, target } of selectedItems) {
      const address =
        target === 'self'
          ? {
              targetFamilyMemberId: familyMemberId,
              targetIsProband: !!isProband
            }
          : memberByKey.get(target)
          ? targetOf(memberByKey.get(target))
          : null;
      if (!address) continue;
      items.push({
        kind: f.kind,
        id: f.id,
        name: f.name,
        status: f.status,
        age: f.age ?? null,
        evidence: (f.evidence || [])[0] || null,
        classification: f.classification || null,
        symbol: f.symbol || null,
        variant: f.variant || null,
        secondHand: !!f.secondHand,
        notSighted: !!f.notSighted,
        subject: subject.key,
        ...address
      });
    }
    if (!items.length) return { success: false };
    setSavingNote(true);
    try {
      const res = await axios.post('/clinical-notes/accept-suggestions', {
        userId: user_id,
        familyMemberId,
        familyId: user.familyId,
        isProband: !!isProband,
        // A calendar date, not an instant: a local-midnight Date sent as ISO
        // is the previous day in UTC at any positive offset.
        clinicalNoteDate: clinicalNoteDate
          ? toStoredDate(clinicalNoteDate)
          : undefined,
        clinician,
        items
      });
      if (res.data.success) {
        // Strip stale aggregates so the reducer recomputes them from updated member data
        /* eslint-disable no-unused-vars */
        const {
          diagnoses: _d,
          phenotypes: _p,
          genotypes: _g,
          ...usersBase
        } = users;
        /* eslint-enable no-unused-vars */
        const memberUpdate = {
          phenotypes: res.data.phenotypes || [],
          genotypes: res.data.genotypes || [],
          diagnoses: res.data.diagnoses || []
        };
        dispatchUpdate({
          type: EVENTS.SET_PEDIGREE_SELECTED_USER,
          value: isProband
            ? {
                ...usersBase,
                selectedUser: { ...users.selectedUser, ...memberUpdate }
              }
            : {
                ...usersBase,
                family: (users.family || []).map((m) =>
                  m.familyMemberId === familyMemberId
                    ? { ...m, ...memberUpdate }
                    : m
                )
              }
        });
        onAccepted?.({
          phenotypes: res.data.phenotypes,
          genotypes: res.data.genotypes,
          diagnoses: res.data.diagnoses
        });
        // Relatives' records changed too: re-read the family so the chart and
        // their sidebars show what was added.
        if (items.some((i) => i.subject !== 'self'))
          await family.refreshFamily?.();
        const failed = (res.data.results || []).filter((r) => r.error);
        setLastAccept({ failed, count: res.data.count });
        setAttachmentAnalyses([]);
        setNoteAnalysis(null);
        setTicks({});
        setTargets({});
        return { success: true, count: res.data.count, failed };
      }
    } catch {
      // caller shows the error
    } finally {
      setSavingNote(false);
    }
    return { success: false };
  };

  // The pre-1845 surface, still read by the forms.
  const suggestions = useMemo(
    () =>
      analysis.subjects.flatMap((s) =>
        s.findings.map((f) => ({ ...f, subject: s.key }))
      ),
    [analysis.subjects]
  );
  const selectedSuggestions = selectedItems.map((x) => x.finding);

  return {
    // per person (TGCLOUD-1845)
    analysis,
    matches,
    members,
    targetFor,
    setTarget,
    isSelected,
    toggleFinding,
    selectedCount: selectedItems.length,
    lastAccept,
    // shared
    suggestions,
    analyzingNote,
    selectedSuggestions,
    savingNote,
    handleTextBlur,
    handleAttachmentText,
    acceptSuggestions,
    analyzeText,
    hpoRelease
  };
}
